import { useState, useEffect, useMemo } from 'react';
import { MapContainer, TileLayer, GeoJSON, useMap } from 'react-leaflet';
import { AlertTriangle, ChevronRight, Info, MapPin, List, RefreshCw } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { ScrollArea } from '../components/ui/scroll-area';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { PageHeader } from '../components/layout/PageHeader';
import { LoadingState } from '../components/common/LoadingState';
import { ErrorState, EmptyState } from '../components/common/ErrorState';
import { GeolocationButton } from '../components/GeolocationButton';
import { RESOURCE_URLS, NIVEL_AVISO_MAP, NIVEL_AVISO_COLORS } from '../constants/ckanResources';
import { fetchWithCache } from '../services/cacheService';
import { API_BASE_URL } from '../config/api';
import { toast } from 'sonner';

// URLs de recursos
const RESOURCES = {
  viña: {
    geojson: RESOURCE_URLS.GEOJSON_COMARCAS_VINA,
    avisos: {
      BOTRYTIS: RESOURCE_URLS.AVISOS_VINA_BOTRYTIS,
      MILDIU: RESOURCE_URLS.AVISOS_VINA_MILDIU,
      OIDIO: RESOURCE_URLS.AVISOS_VINA_OIDIO,
    }
  },
  papa: {
    geojson: RESOURCE_URLS.GEOJSON_COMARCAS_PAPA,
    avisos: {
      MILDIU: RESOURCE_URLS.AVISOS_PAPA_MILDIU,
    }
  }
};

function getColorByNivel(nivel) {
  const nivelNum = typeof nivel === 'string' ? (NIVEL_AVISO_MAP[nivel] || 0) : nivel;
  return NIVEL_AVISO_COLORS[nivelNum] || NIVEL_AVISO_COLORS[0];
}

function getNivelLabel(nivel) {
  if (typeof nivel === 'number') {
    return Object.entries(NIVEL_AVISO_MAP).find(([k, v]) => v === nivel)?.[0] || 'Sin datos';
  }
  return nivel || 'Sin datos';
}

export function AvisosPage() {
  const [cultivo, setCultivo] = useState('viña');
  const [geojsonData, setGeojsonData] = useState(null);
  const [avisosData, setAvisosData] = useState({});
  const [loading, setLoading] = useState(true);
  const [loadingGeo, setLoadingGeo] = useState(false);
  const [geoError, setGeoError] = useState(false);
  const [error, setError] = useState(null);
  const [selectedComarca, setSelectedComarca] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [activeView, setActiveView] = useState('lista'); // Lista por defecto

  useEffect(() => {
    loadAvisos();
  }, [cultivo]);

  // Cargar GeoJSON de forma separada (solo cuando se necesita el mapa)
  useEffect(() => {
    if (activeView === 'mapa' && !geojsonData && !loadingGeo && !geoError) {
      loadGeoJSON();
    }
  }, [activeView, geojsonData, loadingGeo, geoError, cultivo]);

  // Limpiar GeoJSON cuando cambia el cultivo
  useEffect(() => {
    setGeojsonData(null);
    setGeoError(false);
  }, [cultivo]);

  async function loadGeoJSON() {
    const resources = RESOURCES[cultivo];
    const PROXY_URL = `${API_BASE_URL}/api/ckan-proxy`;
    
    setLoadingGeo(true);
    setGeoError(false);
    
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 60000); // 60s timeout
      
      const geoResponse = await fetch(
        `${PROXY_URL}?url=${encodeURIComponent(resources.geojson)}`,
        { signal: controller.signal }
      );
      clearTimeout(timeoutId);
      
      if (geoResponse.ok) {
        const geoData = await geoResponse.json();
        setGeojsonData(geoData);
      } else {
        console.warn('Error HTTP cargando GeoJSON:', geoResponse.status);
        setGeoError(true);
      }
    } catch (geoErr) {
      console.warn('No se pudo cargar el GeoJSON del mapa:', geoErr);
      setGeoError(true);
    } finally {
      setLoadingGeo(false);
    }
  }

  async function loadAvisos() {
    setLoading(true);
    setError(null);
    setSelectedComarca(null);
    
    try {
      const resources = RESOURCES[cultivo];
      const PROXY_URL = `${API_BASE_URL}/api/ckan-proxy`;
      
      // Cargar todos los avisos en paralelo con caché
      const avisosPromises = Object.entries(resources.avisos).map(async ([enfermedad, url]) => {
        try {
          const fullUrl = `${PROXY_URL}?url=${encodeURIComponent(url)}`;
          const result = await fetchWithCache(fullUrl);
          return { enfermedad, data: result.data };
        } catch (e) {
          console.warn(`Error cargando avisos de ${enfermedad}:`, e);
          return { enfermedad, data: null };
        }
      });
      
      const avisosResults = await Promise.all(avisosPromises);
      const avisosMap = {};
      avisosResults.forEach(({ enfermedad, data }) => {
        if (data) avisosMap[enfermedad] = data;
      });
      setAvisosData(avisosMap);
      
      // Verificar si tenemos al menos algunos avisos
      if (Object.keys(avisosMap).length === 0) {
        setError('No se pudieron cargar los datos de avisos. Por favor, intenta de nuevo.');
      }
      
    } catch (e) {
      console.error('Error cargando datos:', e);
      setError('No se pudieron cargar los datos de avisos. Por favor, intenta de nuevo.');
    } finally {
      setLoading(false);
    }
  }

  function handleRetry() {
    if (activeView === 'mapa' && geoError) {
      loadGeoJSON();
    } else {
      loadAvisos();
    }
  }

  // Procesar avisos por comarca/zona
  const avisosPorComarca = useMemo(() => {
    if (!geojsonData?.features || Object.keys(avisosData).length === 0) return [];
    
    const comarcasMap = new Map();
    
    // Procesar cada feature del GeoJSON
    geojsonData.features.forEach(feature => {
      const props = feature.properties;
      const comarcaId = props.comarca_zona_id || props.comarca_id;
      const comarcaNombre = props.comarca_nombre;
      const zona = props.zona || null;
      
      const key = `${comarcaId}`;
      
      if (!comarcasMap.has(key)) {
        comarcasMap.set(key, {
          id: comarcaId,
          nombre: comarcaNombre,
          zona,
          avisos: [],
          riesgoMaximo: 0,
          feature
        });
      }
    });
    
    // Agregar avisos a cada comarca
    Object.entries(avisosData).forEach(([enfermedad, data]) => {
      if (!data?.cultivos) return;
      
      data.cultivos.forEach(cultivoData => {
        cultivoData.plagas?.forEach(plaga => {
          if (plaga.plaga_enfermedad !== enfermedad) return;
          
          plaga.comarcas?.forEach(comarca => {
            comarca.zonas?.forEach(zona => {
              const comarcaEntry = comarcasMap.get(`${zona.comarca_zona_id}`);
              if (!comarcaEntry) return;
              
              // Obtener avisos más recientes (últimos 30 días)
              const avisosRecientes = (zona.avisos || [])
                .filter(a => {
                  const fechaAviso = new Date(a.fecha_aviso);
                  const hace30dias = new Date();
                  hace30dias.setDate(hace30dias.getDate() - 30);
                  return fechaAviso >= hace30dias;
                })
                .map(a => ({
                  ...a,
                  enfermedad,
                  comarca: comarca.comarca_nombre,
                  zona: zona.comarca_zona,
                  nivelNum: NIVEL_AVISO_MAP[a.nivel_aviso] || 0
                }));
              
              comarcaEntry.avisos.push(...avisosRecientes);
              
              // Actualizar riesgo máximo
              const maxNivel = Math.max(
                comarcaEntry.riesgoMaximo,
                ...avisosRecientes.map(a => a.nivelNum)
              );
              comarcaEntry.riesgoMaximo = maxNivel;
            });
          });
        });
      });
    });
    
    // Si no hay avisos recientes, buscar el más reciente de cada comarca
    comarcasMap.forEach((comarca, key) => {
      if (comarca.avisos.length === 0) {
        // Buscar último aviso disponible
        Object.entries(avisosData).forEach(([enfermedad, data]) => {
          if (!data?.cultivos) return;
          
          data.cultivos.forEach(cultivoData => {
            cultivoData.plagas?.forEach(plaga => {
              plaga.comarcas?.forEach(comarcaData => {
                comarcaData.zonas?.forEach(zona => {
                  if (zona.comarca_zona_id !== comarca.id) return;
                  
                  const ultimoAviso = zona.avisos?.[0];
                  if (ultimoAviso) {
                    comarca.avisos.push({
                      ...ultimoAviso,
                      enfermedad,
                      comarca: comarcaData.comarca_nombre,
                      zona: zona.comarca_zona,
                      nivelNum: NIVEL_AVISO_MAP[ultimoAviso.nivel_aviso] || 0
                    });
                    comarca.riesgoMaximo = Math.max(
                      comarca.riesgoMaximo,
                      NIVEL_AVISO_MAP[ultimoAviso.nivel_aviso] || 0
                    );
                  }
                });
              });
            });
          });
        });
      }
    });
    
    return Array.from(comarcasMap.values())
      .filter(c => c.avisos.length > 0 || c.nombre)
      .sort((a, b) => b.riesgoMaximo - a.riesgoMaximo);
  }, [geojsonData, avisosData]);

  // Lista de todos los avisos individuales ordenados por fecha (más reciente primero)
  const todosLosAvisos = useMemo(() => {
    const avisos = [];
    
    // Procesar datos de avisos - Soporta dos estructuras:
    // 1. Viña: {cultivos: [{plagas: [{comarcas: [{zonas: [{avisos}]}]}]}]}
    // 2. Papa: {cultivos: [{plagas: [{comarcas: [{avisos}]}]}]}
    Object.entries(avisosData).forEach(([enfermedad, data]) => {
      if (!data) return;
      
      const cultivos = data.cultivos || [];
      cultivos.forEach(cultivoItem => {
        const plagas = cultivoItem.plagas || [];
        plagas.forEach(plaga => {
          const plagaNombre = plaga.plaga_enfermedad || enfermedad;
          const comarcas = plaga.comarcas || [];
          
          comarcas.forEach(comarca => {
            const comarcaNombre = comarca.comarca_nombre;
            const zonas = comarca.zonas || [];
            
            // Caso 1: Datos con zonas (viña)
            if (zonas.length > 0) {
              zonas.forEach(zona => {
                const zonaNombre = zona.comarca_zona;
                const avisosZona = zona.avisos || [];
                
                // Solo tomar el aviso más reciente de cada zona
                if (avisosZona.length > 0) {
                  const avisoReciente = avisosZona[0];
                  const nivelStr = avisoReciente.nivel_aviso || '';
                  const nivelNum = NIVEL_AVISO_MAP[nivelStr] ?? 0;
                  
                  avisos.push({
                    enfermedad: plagaNombre,
                    nivel_aviso: nivelStr,
                    nivelNum: nivelNum,
                    fecha_aviso: avisoReciente.fecha_aviso,
                    comarcaNombre: comarcaNombre,
                    zonaNombre: zonaNombre ? `${comarcaNombre} - ${zonaNombre}` : comarcaNombre,
                  });
                }
              });
            } else {
              // Caso 2: Datos sin zonas, avisos directamente en comarca (papa)
              const avisosComarca = comarca.avisos || [];
              if (avisosComarca.length > 0) {
                const avisoReciente = avisosComarca[0];
                const nivelStr = avisoReciente.nivel_aviso || '';
                const nivelNum = NIVEL_AVISO_MAP[nivelStr] ?? 0;
                
                avisos.push({
                  enfermedad: plagaNombre,
                  nivel_aviso: nivelStr,
                  nivelNum: nivelNum,
                  fecha_aviso: avisoReciente.fecha_aviso,
                  comarcaNombre: comarcaNombre,
                  zonaNombre: comarcaNombre,
                });
              }
            }
          });
        });
      });
    });
    
    // Ordenar por fecha descendente (más reciente primero)
    return avisos.sort((a, b) => {
      const fechaA = new Date(a.fecha_aviso);
      const fechaB = new Date(b.fecha_aviso);
      return fechaB - fechaA;
    });
  }, [avisosData]);

  // GeoJSON con datos de riesgo
  const geojsonWithRiesgo = useMemo(() => {
    if (!geojsonData) return null;
    
    return {
      ...geojsonData,
      features: geojsonData.features.map(feature => {
        const comarcaId = feature.properties.comarca_zona_id || feature.properties.comarca_id;
        const comarcaData = avisosPorComarca.find(c => c.id === comarcaId);
        
        return {
          ...feature,
          properties: {
            ...feature.properties,
            _riesgoMaximo: comarcaData?.riesgoMaximo || 0,
            _numAvisos: comarcaData?.avisos?.length || 0,
            _avisos: comarcaData?.avisos || []
          }
        };
      })
    };
  }, [geojsonData, avisosPorComarca]);

  function handleComarcaClick(comarca) {
    const comarcaData = avisosPorComarca.find(c => 
      c.id === comarca.comarca_zona_id || c.id === comarca.comarca_id
    );
    
    setSelectedComarca({
      ...comarca,
      avisos: comarcaData?.avisos || [],
      riesgoMaximo: comarcaData?.riesgoMaximo || 0
    });
    setShowDetailModal(true);
  }

  function onEachFeature(feature, layer) {
    const props = feature.properties;
    const riesgoMax = props._riesgoMaximo || 0;
    const numAvisos = props._numAvisos || 0;
    const nombreCompleto = props.zona 
      ? `${props.comarca_nombre} - ${props.zona}`
      : props.comarca_nombre;
    
    layer.bindTooltip(`
      <strong>${nombreCompleto}</strong><br/>
      ${numAvisos} aviso(s) · Riesgo: ${getNivelLabel(riesgoMax)}
    `, { sticky: true });
    
    layer.on('click', () => {
      handleComarcaClick(props);
    });
  }

  function styleFeature(feature) {
    const riesgo = feature.properties._riesgoMaximo || 0;
    return {
      fillColor: getColorByNivel(riesgo),
      weight: 2,
      opacity: 1,
      color: '#2F5233',
      fillOpacity: 0.6
    };
  }

  if (loading) {
    return (
      <div className="px-4 py-4">
        <PageHeader title="Avisos Fitosanitarios" />
        <LoadingState message="Cargando avisos..." />
      </div>
    );
  }

  if (error) {
    return (
      <div className="px-4 py-4">
        <PageHeader title="Avisos Fitosanitarios" />
        <ErrorState message={error} onRetry={loadAvisos} />
      </div>
    );
  }

  return (
    <div className="px-4 py-4" data-testid="avisos-page">
      <PageHeader 
        title="Avisos Fitosanitarios" 
        subtitle={`Cultivo: ${cultivo.charAt(0).toUpperCase() + cultivo.slice(1)} · Datos reales`}
        showBack
      />

      {/* Selector de cultivo */}
      <Tabs value={cultivo} onValueChange={setCultivo} className="mt-4">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="viña" data-testid="tab-vina">
            Viña
          </TabsTrigger>
          <TabsTrigger value="papa" data-testid="tab-papa">
            Papa
          </TabsTrigger>
        </TabsList>
      </Tabs>

      {/* Disclaimer legal */}
      <Alert className="mt-4 bg-amber-50 border-amber-200">
        <Info className="h-4 w-4 text-amber-600" />
        <AlertDescription className="text-amber-800 text-sm">
          Información orientativa. Consulte asesoramiento técnico antes de realizar tratamientos.
        </AlertDescription>
      </Alert>

      {/* Toggle vista */}
      <div className="flex gap-2 mt-4">
        <Button
          variant={activeView === 'mapa' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setActiveView('mapa')}
          data-testid="view-mapa"
        >
          <MapPin size={16} className="mr-1" />
          Mapa
        </Button>
        <Button
          variant={activeView === 'lista' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setActiveView('lista')}
          data-testid="view-lista"
        >
          <List size={16} className="mr-1" />
          Lista
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={handleRetry}
          className="ml-auto"
          title="Recargar datos"
        >
          <RefreshCw size={16} />
        </Button>
      </div>

      {/* Vista mapa */}
      {activeView === 'mapa' && (
        <>
          {loadingGeo && (
            <div className="mt-4 h-[350px] rounded-xl overflow-hidden border border-border relative flex items-center justify-center bg-muted/30">
              <div className="text-center">
                <RefreshCw className="h-8 w-8 animate-spin mx-auto text-muted-foreground" />
                <p className="mt-2 text-sm text-muted-foreground">Cargando mapa de comarcas...</p>
                <p className="text-xs text-muted-foreground mt-1">(Este proceso puede tardar un momento)</p>
              </div>
            </div>
          )}
          
          {geoError && !loadingGeo && (
            <div className="mt-4 h-[350px] rounded-xl overflow-hidden border border-border relative flex items-center justify-center bg-muted/30">
              <div className="text-center p-4">
                <AlertTriangle className="h-8 w-8 mx-auto text-amber-500" />
                <p className="mt-2 text-sm font-medium">No se pudo cargar el mapa</p>
                <p className="text-xs text-muted-foreground mt-1">El servidor de datos está temporalmente no disponible</p>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={loadGeoJSON}
                  className="mt-3"
                >
                  <RefreshCw className="h-4 w-4 mr-1" />
                  Reintentar
                </Button>
                <p className="text-xs text-muted-foreground mt-2">
                  Puedes ver los avisos en la pestaña &quot;Lista&quot;
                </p>
              </div>
            </div>
          )}
          
          {!loadingGeo && !geoError && geojsonWithRiesgo && (
            <div className="mt-4 h-[350px] rounded-xl overflow-hidden border border-border relative">
              <MapContainer 
                center={[28.3, -16.55]} 
                zoom={9} 
                className="h-full w-full"
                scrollWheelZoom={true}
              >
                <TileLayer
                  attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />
                <GeolocationButton />
                <GeoJSON 
                  key={`${cultivo}-${Object.keys(avisosData).length}`}
                  data={geojsonWithRiesgo}
                  style={styleFeature}
                  onEachFeature={onEachFeature}
                />
              </MapContainer>
            </div>
          )}
        </>
      )}

      {/* Leyenda de colores */}
      {activeView === 'mapa' && !loadingGeo && !geoError && geojsonWithRiesgo && (
        <div className="mt-3 flex items-center gap-4 justify-center text-sm">
          <div className="flex items-center gap-1">
            <div className="w-4 h-4 rounded" style={{ backgroundColor: NIVEL_AVISO_COLORS[3] }}></div>
            <span>Alto</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-4 h-4 rounded" style={{ backgroundColor: NIVEL_AVISO_COLORS[2] }}></div>
            <span>Medio</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-4 h-4 rounded" style={{ backgroundColor: NIVEL_AVISO_COLORS[1] }}></div>
            <span>Bajo</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-4 h-4 rounded" style={{ backgroundColor: NIVEL_AVISO_COLORS[0] }}></div>
            <span>Sin alerta</span>
          </div>
        </div>
      )}

      {/* Vista lista - Avisos ordenados por fecha */}
      {activeView === 'lista' && (
        <ScrollArea className="mt-4 h-[400px]">
          {todosLosAvisos.length === 0 ? (
            <EmptyState 
              title="Sin avisos"
              message="No hay avisos fitosanitarios disponibles para este cultivo"
              icon={AlertTriangle}
            />
          ) : (
            <div className="space-y-3">
              {todosLosAvisos.map((aviso, index) => (
                <Card 
                  key={`aviso-${index}`}
                  className="border-l-4"
                  style={{ borderLeftColor: getColorByNivel(aviso.nivelNum) }}
                  data-testid={`aviso-card-${index}`}
                >
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between mb-2">
                      <Badge 
                        variant={aviso.nivelNum >= 3 ? 'destructive' : aviso.nivelNum >= 2 ? 'default' : 'secondary'}
                      >
                        {aviso.nivel_aviso}
                      </Badge>
                      <span className="text-sm text-muted-foreground">
                        {new Date(aviso.fecha_aviso).toLocaleDateString('es-ES', {
                          day: 'numeric',
                          month: 'short',
                          year: 'numeric'
                        })}
                      </span>
                    </div>
                    <h4 className="font-semibold">{aviso.enfermedad}</h4>
                    <div className="mt-2 flex items-center gap-2 text-sm text-muted-foreground">
                      <MapPin className="h-4 w-4" />
                      <span>
                        {aviso.zonaNombre || aviso.comarcaNombre}
                        {aviso.zonaNombre && aviso.comarcaNombre && aviso.zonaNombre !== aviso.comarcaNombre && (
                          <span className="text-xs ml-1">({aviso.comarcaNombre})</span>
                        )}
                      </span>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </ScrollArea>
      )}

      {/* Enfermedades disponibles */}
      <div className="mt-4 text-center text-xs text-muted-foreground">
        Enfermedades: {Object.keys(avisosData).join(', ') || 'Cargando...'}
      </div>

      {/* Modal detalle de comarca */}
      <Dialog open={showDetailModal} onOpenChange={setShowDetailModal}>
        <DialogContent className="max-w-md max-h-[80vh] overflow-y-auto z-[1000]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-500" />
              {selectedComarca?.comarca_nombre}
              {selectedComarca?.zona && (
                <span className="text-sm font-normal text-muted-foreground">
                  ({selectedComarca.zona})
                </span>
              )}
            </DialogTitle>
            <DialogDescription>
              Avisos fitosanitarios para {cultivo}
            </DialogDescription>
          </DialogHeader>
          
          {selectedComarca?.avisos?.length > 0 ? (
            <div className="space-y-3 mt-4">
              {selectedComarca.avisos.slice(0, 10).map((aviso, idx) => (
                <Card 
                  key={idx} 
                  className="border-l-4" 
                  style={{ borderLeftColor: getColorByNivel(aviso.nivelNum) }}
                >
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between mb-2">
                      <Badge 
                        variant={aviso.nivelNum >= 3 ? 'destructive' : aviso.nivelNum >= 2 ? 'default' : 'secondary'}
                      >
                        {aviso.nivel_aviso}
                      </Badge>
                      <span className="text-sm text-muted-foreground">
                        {new Date(aviso.fecha_aviso).toLocaleDateString('es-ES')}
                      </span>
                    </div>
                    <h4 className="font-semibold">{aviso.enfermedad}</h4>
                    {aviso.zona && (
                      <p className="text-sm text-muted-foreground mt-1">{aviso.zona}</p>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <EmptyState 
              title="Sin avisos recientes"
              message="No hay avisos fitosanitarios recientes para esta comarca"
              icon={AlertTriangle}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Fuente de datos */}
      <div className="mt-4 text-center text-xs text-muted-foreground">
        Fuente: Datos abiertos del Cabildo de Tenerife
      </div>
    </div>
  );
}

export default AvisosPage;
